import { z } from 'zod'
import { oc } from '@orpc/contract'
import { kateLanguageSchema } from './kate-session.contract'

export const kateCheckInStatusSchema = z.enum([
  'not_sent',
  'sent',
  'opened',
  'in_progress',
  'completed',
  'reviewed',
  'failed',
])
export type KateCheckInStatusValue = z.infer<typeof kateCheckInStatusSchema>

/**
 * What the call settled, separate from where the check-in stands: a completed
 * check-in that moved the visit and one that needs a coordinator are both
 * `completed`, and staff need to tell them apart at a glance.
 */
export const kateCheckInOutcomeSchema = z.enum([
  'confirmed',
  'rescheduled',
  /** A missed visit's replacement: a new appointment, the no-show left as it is. */
  'rebooked',
  'cancelled',
  'declined',
  'needs_assistance',
  'unchanged',
])
export type KateCheckInOutcome = z.infer<typeof kateCheckInOutcomeSchema>

const kateCheckInViaSchema = z.enum(['sms', 'email', 'link'])

export const kateCheckInRowSchema = z.object({
  key: z.string(),
  label: z.string(),
  tone: z.enum(['ok', 'attention']),
  needsReview: z.boolean(),
})

/**
 * Staff view of one appointment's Kate check-in for the schedule card: derived
 * status, the step timestamps, and the filed answers as display rows. Practitioner
 * callers only; the caller's own Medplum client does every read, so org
 * visibility is the access policy's, not ours.
 */
export const kateCheckInGet = oc
  .route({ method: 'POST', path: '/v2/kate/check-in/status' })
  .input(z.object({ appointmentId: z.string() }))
  .output(
    z.object({
      status: kateCheckInStatusSchema,
      sentAt: z.string().optional(),
      sentVia: kateCheckInViaSchema.optional(),
      openedAt: z.string().optional(),
      startedAt: z.string().optional(),
      completedAt: z.string().optional(),
      reviewedAt: z.string().optional(),
      reviewedByName: z.string().optional(),
      /** Absent until the call is filed. */
      outcome: kateCheckInOutcomeSchema.optional(),
      /** Where a visit Kate moved came from, and where it went. */
      rescheduledFrom: z.string().optional(),
      rescheduledTo: z.string().optional(),
      /** The Care Queue row a coordinator finishes the call from, when one was created. */
      followUpTask: z.object({ boardId: z.string(), itemId: z.string() }).optional(),
      /**
       * Present only on a missed visit: where its rebooking stands, so the card
       * can offer the invite and then say what came of it.
       */
      rebooking: z
        .object({
          /** The follow-up's own lifecycle, apart from any earlier pre-visit call on the same visit. */
          status: kateCheckInStatusSchema,
          invitedAt: z.string().optional(),
          attempts: z.number().int().nonnegative(),
          bookedAt: z.string().optional(),
          declinedAt: z.string().optional(),
          /** The replacement visit Kate booked, for the card to show and jump to. */
          appointment: z
            .object({
              id: z.string(),
              encounterId: z.string().optional(),
              start: z.string(),
              end: z.string().optional(),
              providerName: z.string().optional(),
            })
            .optional(),
        })
        .optional(),
      rows: z.array(kateCheckInRowSchema),
      summary: z.string().optional(),
      needsReview: z.number().int().nonnegative(),
      responseId: z.string().optional(),
      transcript: z
        .object({ documentReferenceId: z.string(), url: z.string().optional() })
        .optional(),
    }),
  )

/**
 * Mints the pre-visit magic link for the appointment's patient. `deliver: true`
 * texts/emails it (the "Send / Resend check-in" buttons); `deliver: false` only
 * returns it (the "Copy link" button, for staff handing it over on the phone).
 * Either way the check-in is stamped as sent. This is the ONLY way an invite goes
 * out — there is no scheduled sweep.
 */
/**
 * `rebooking` is the same voice call aimed at a visit that was missed: it is the
 * only mode allowed once the appointment is `noshow`, and the call books a NEW
 * appointment rather than touching the missed one.
 */
export const kateCheckInModeSchema = z.enum(['check-in', 'rebooking'])
export type KateCheckInMode = z.infer<typeof kateCheckInModeSchema>

export const kateCheckInSend = oc
  .route({ method: 'POST', path: '/v2/kate/check-in/send' })
  .input(
    z.object({
      appointmentId: z.string(),
      deliver: z.boolean().default(true),
      mode: kateCheckInModeSchema.default('check-in'),
    }),
  )
  .output(
    z.object({
      link: z.string(),
      delivered: z.array(z.enum(['sms', 'email'])),
      sentAt: z.string(),
      sentVia: kateCheckInViaSchema,
    }),
  )

/** A clinician has read the responses; the card moves to "Reviewed". */
export const kateCheckInReview = oc
  .route({ method: 'POST', path: '/v2/kate/check-in/review' })
  .input(z.object({ appointmentId: z.string() }))
  .output(z.object({ reviewedAt: z.string() }))

/**
 * The patient reached the pre-visit page. Public with the same proof rules as
 * session create (bearer or magic link, never anonymous); it only stamps a
 * first-open timestamp, so it is the cheapest write on the public surface.
 */
export const kateCheckInOpened = oc
  .route({ method: 'POST', path: '/v2/kate/check-in/opened' })
  .input(
    z.object({
      encounterId: z.string(),
      magicLink: z.object({ loginId: z.string(), code: z.string() }).optional(),
    }),
  )
  .output(z.object({ ok: z.literal(true), preferredLanguage: kateLanguageSchema }))
